import { useEffect, useState } from 'react'
import { Globe, Plus } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useAttachFromGlobalTool, useCreateTool } from '@/features/projects/use-catalog'
import { useAttachTool } from '@/features/projects/use-step-attachments'
import { useGlobalCatalogSearch } from '@/features/catalog/use-global-catalog'
import type { CatalogTool, GlobalCatalogTool } from '@/api/types'

interface ToolPickerProps {
  stepId: string
  projectId: string
  catalog: CatalogTool[]
  attachedIds: string[]
}

type Mode =
  | { kind: 'list' }
  | { kind: 'createNew'; name: string }
  | { kind: 'searchGlobal' }
  | { kind: 'attachFromGlobal'; global: GlobalCatalogTool }

const dollarsToCents = (input: string): number | null => {
  const trimmed = input.trim()
  if (!trimmed) return null
  const num = Number(trimmed)
  if (!Number.isFinite(num) || num < 0) return null
  return Math.round(num * 100)
}

export const ToolPicker = ({ stepId, projectId, catalog, attachedIds }: ToolPickerProps) => {
  const [open, setOpen] = useState(false)
  const [mode, setMode] = useState<Mode>({ kind: 'list' })
  const [search, setSearch] = useState('')
  const [priceDollars, setPriceDollars] = useState('0')

  const create = useCreateTool(projectId)
  const attachFromGlobal = useAttachFromGlobalTool(projectId)
  const attach = useAttachTool(projectId)
  const [globalQuery, setGlobalQuery] = useState('')
  const globalSearch = useGlobalCatalogSearch('TOOL', globalQuery, mode.kind === 'searchGlobal')

  useEffect(() => {
    if (!open) {
      setMode({ kind: 'list' })
      setSearch('')
      setPriceDollars('0')
      setGlobalQuery('')
    }
  }, [open])

  const available = catalog.filter((t) => !attachedIds.includes(t.id))
  const trimmed = search.trim()
  const exactMatch = available.find((t) => t.name.toLowerCase() === trimmed.toLowerCase())

  const attachExisting = async (tool: CatalogTool) => {
    try {
      await attach.mutateAsync({ stepId, data: { catalogToolId: tool.id } })
      setOpen(false)
    } catch {
      toast.error('Failed to attach tool')
    }
  }

  const createAndAttach = async () => {
    const name = (mode.kind === 'createNew' ? mode.name : '').trim()
    if (!name) {
      toast.error('Name is required')
      return
    }
    const cents = dollarsToCents(priceDollars)
    if (cents === null) {
      toast.error('Price must be a non-negative number')
      return
    }
    try {
      const tool = await create.mutateAsync({ name, unitPriceCents: cents })
      await attach.mutateAsync({ stepId, data: { catalogToolId: tool.id } })
      setOpen(false)
    } catch {
      toast.error('Failed to create + attach tool')
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1">
          <Plus className="h-3 w-3" />
          Tool
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        {mode.kind === 'list' && (
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search or add a tool…"
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              {available.length === 0 && !trimmed && (
                <CommandEmpty>No tools yet. Type a name to create one.</CommandEmpty>
              )}
              {available.length > 0 && (
                <CommandGroup heading="Catalog">
                  {available
                    .filter((t) =>
                      trimmed === '' ? true : t.name.toLowerCase().includes(trimmed.toLowerCase())
                    )
                    .map((t) => (
                      <CommandItem
                        key={t.id}
                        value={t.name}
                        onSelect={() => void attachExisting(t)}
                      >
                        <span className="flex-1">{t.name}</span>
                        <span className="text-xs text-muted-foreground">
                          ${(t.unitPriceCents / 100).toFixed(2)}
                        </span>
                      </CommandItem>
                    ))}
                </CommandGroup>
              )}
              {trimmed && !exactMatch && (
                <CommandGroup heading="Add new">
                  <CommandItem
                    value={`__create__${trimmed}`}
                    onSelect={() => setMode({ kind: 'createNew', name: trimmed })}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Create &ldquo;{trimmed}&rdquo;
                  </CommandItem>
                </CommandGroup>
              )}
              <CommandGroup heading="Global catalog">
                <CommandItem
                  value="__search-global__"
                  onSelect={() => {
                    setGlobalQuery(trimmed)
                    setMode({ kind: 'searchGlobal' })
                  }}
                >
                  <Globe className="mr-2 h-4 w-4" />
                  Search shared catalog
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        )}

        {mode.kind === 'searchGlobal' && (
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search the shared catalog…"
              value={globalQuery}
              onValueChange={setGlobalQuery}
            />
            <CommandList>
              {globalSearch.isLoading && <CommandEmpty>Searching…</CommandEmpty>}
              {!globalSearch.isLoading && (globalSearch.data?.items.length ?? 0) === 0 && (
                <CommandEmpty>No matches in the shared catalog.</CommandEmpty>
              )}
              {globalSearch.data && globalSearch.data.items.length > 0 && (
                <CommandGroup heading="Shared catalog">
                  {globalSearch.data.items.map((g) => (
                    <CommandItem
                      key={g.id}
                      value={g.id}
                      onSelect={() => setMode({ kind: 'attachFromGlobal', global: g })}
                    >
                      <span className="flex-1">{g.name}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
            <div className="border-t border-border p-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setMode({ kind: 'list' })}
              >
                Back
              </Button>
            </div>
          </Command>
        )}

        {mode.kind === 'attachFromGlobal' && (
          <form
            onSubmit={async (e) => {
              e.preventDefault()
              const cents = dollarsToCents(priceDollars)
              if (cents === null) {
                toast.error('Price must be a non-negative number')
                return
              }
              try {
                const created = await attachFromGlobal.mutateAsync({
                  globalCatalogToolId: mode.global.id,
                  unitPriceCents: cents,
                })
                await attach.mutateAsync({ stepId, data: { catalogToolId: created.id } })
                setOpen(false)
              } catch {
                toast.error('Failed to attach from shared catalog')
              }
            }}
            className="space-y-3 p-3"
          >
            <div>
              <p className="text-sm font-medium">{mode.global.name}</p>
              <p className="text-xs text-muted-foreground">Shared catalog</p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="tool-price-from-global">Price (USD)</Label>
              <Input
                id="tool-price-from-global"
                autoFocus
                type="number"
                step="0.01"
                min="0"
                value={priceDollars}
                onChange={(e) => setPriceDollars(e.target.value)}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setMode({ kind: 'searchGlobal' })}
              >
                Back
              </Button>
              <Button
                type="submit"
                size="sm"
                disabled={attachFromGlobal.isPending || attach.isPending}
              >
                Use + attach
              </Button>
            </div>
          </form>
        )}

        {mode.kind === 'createNew' && (
          <form
            onSubmit={(e) => {
              e.preventDefault()
              void createAndAttach()
            }}
            className="space-y-3"
          >
            <div>
              <p className="text-sm font-medium">New tool</p>
              <p className="text-xs text-muted-foreground">{mode.name}</p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="tool-price">Price (USD)</Label>
              <Input
                id="tool-price"
                autoFocus
                type="number"
                step="0.01"
                min="0"
                value={priceDollars}
                onChange={(e) => setPriceDollars(e.target.value)}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setMode({ kind: 'list' })}
              >
                Back
              </Button>
              <Button type="submit" size="sm" disabled={create.isPending || attach.isPending}>
                Create + attach
              </Button>
            </div>
          </form>
        )}
      </PopoverContent>
    </Popover>
  )
}
